// idbStore.js — IndexedDB local persistence (simple key-value)
// Raw dataset rows stay here; only derived stats ever go to the server.
// One key per module — no sessions, no workspace copies, no version copies.
//
// DB:    dd-lab-idb-v1
// Store: sessions  (keyPath: 'key')
// Shape: { key: string, data: any, savedAt: ISO }

const DB_NAME    = 'dd-lab-idb-v1';
const DB_VERSION = 1;
const STORE      = 'sessions';

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'key' });
      }
    };
    req.onsuccess = (e) => resolve(e.target.result);
    req.onerror   = (e) => reject(e.target.error);
  });
}

/** Persist data under key. Overwrites any existing record. */
export async function idbSave(key, data) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put({ key, data, savedAt: new Date().toISOString() });
    tx.oncomplete = () => resolve();
    tx.onerror    = (e) => reject(e.target.error);
  });
}

/** Load data for key. Returns null if not found. */
export async function idbLoad(key) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx  = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).get(key);
    req.onsuccess = (e) => resolve(e.target.result?.data ?? null);
    req.onerror   = (e) => reject(e.target.error);
  });
}

/** Delete a record. No-op if key not found. */
export async function idbDelete(key) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror    = (e) => reject(e.target.error);
  });
}

/** Return all records whose key starts with prefix. */
export async function idbScan(prefix) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx      = db.transaction(STORE, 'readonly');
    const store   = tx.objectStore(STORE);
    const range   = IDBKeyRange.bound(prefix, prefix + '\uffff', false, false);
    const results = [];
    const req     = store.openCursor(range);
    req.onsuccess = (e) => {
      const cursor = e.target.result;
      if (cursor) { results.push(cursor.value); cursor.continue(); }
      else resolve(results);
    };
    req.onerror = (e) => reject(e.target.error);
  });
}

/**
 * Check if local IDB rows match the expected column headers.
 * Returns the rows array if ≥80% of expected headers are present, or [] if not.
 */
export function validateRowHeaders(rows, expectedHeaders) {
  if (!Array.isArray(rows) || !rows.length) return [];
  if (!Array.isArray(expectedHeaders) || !expectedHeaders.length) return [];
  const sampleRow = rows[0];
  if (!sampleRow || typeof sampleRow !== 'object') return [];
  const rowKeys = Object.keys(sampleRow);
  const matchCount = expectedHeaders.filter(h => rowKeys.includes(h)).length;
  return matchCount >= expectedHeaders.length * 0.8 ? rows : [];
}
